<script lang="ts" setup>
import AccountsDropdown from '@/components/general/AccountsDropdown.vue';
import type {
  ISearchContainerEvents,
  ISearchContainerProps,
  ISearchFocusIndices,
  ISearchQuickChip
} from '@/types/components/general/searchContainer';
import { useI18n } from 'vue-i18n';
import { computed, isRef, nextTick, reactive, ref, toValue, watch } from 'vue';
import { useEventBus } from '@vueuse/core';
import { ROUTE_NAMES } from '@/router/conf';
import { useRoute, useRouter } from 'vue-router';
import useSearchService from '@/composables/apiServices/useSearchService';
import useUserService from '@/composables/apiServices/useUserService';
import {
  addMultiWordGrouping,
  cleanMultiWordSearchQuery,
  getMatchingSearchKeywords,
  isSearchSizeStepValid
} from '@/helpers/search';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { VCard, VProgressCircular, VTextField } from 'vuetify/components';
import type {
  IRecentSearchEmailDetails,
  IRecentSearchResponse
} from '@/types/services/search.service';
import MailboxItemDate from '@/components/mailbox/date/MailboxItemDate.vue';
import useMailboxSharedProvides from '@/composables/mailbox/useMailboxSharedProvides';
import { openAdvancedSearchEventKey } from '@/composables/useEventBus';
import FilterDetails from '@/components/settings/filters/FilterDetails.vue';
import FilterActions from '@/components/settings/filters/FilterActions.vue';
import useEditDialog, {
  mapFilterFormInitialValues
} from '@/composables/filter-shared/useEditFilter';
import type { IFilterDetailsModel } from '@/types/composables/filter-shared/useFilterDetails';
import type { ISettingsParsedFilterItem } from '@/types/services/filter.service';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import { EFolderSystemSpecialUse } from '@/types/services/folder.service.enums';
import type { IFilterActionsModel } from '@/types/composables/filter-shared/useFilterActions';
import SearchInstructions from './SearchInstructions.vue';
import { registerToggleSearchContainer } from '@/composables/onBoarding/useOnboarding';
import { useDisplay } from 'vuetify';

dayjs.extend(utc);
dayjs.extend(timezone);

const props = defineProps<ISearchContainerProps>();
const emit = defineEmits<ISearchContainerEvents>();

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { userIndex } = useAuthenticatedQuery();
const { mobile } = useDisplay();

const { details } = useUserService();
const { data: userDetails } = details();
const { getFlatFolders } = useFoldersService();
const { suspense: flatFoldersSuspense } = getFlatFolders();

const { provideMailboxUserDetails } = useMailboxSharedProvides();
provideMailboxUserDetails();

const isSearchDropdownDisplayed = ref(false);
// Vuetify (>= 4.1) restores focus to the search input after the dropdown
// closes (VOverlay's returnFocusToActivator). The input's focus handler
// would then immediately reopen the dropdown - on mobile the reopened
// full-screen container hides the search results list. While the dropdown
// is closing, suppress that one spurious focus.
let isSuppressingRestoredSearchInputFocus = false;
const isDisplayingAdvancedSearch = ref(false);
const isDisplayingAdvancedSearchDetails = ref(false);

const { errors, query, actions, setValues, handleSubmitFilter, isPending, resetForm } =
  useEditDialog(null);

const shouldQuerySearchMeta = computed(() => isSearchDropdownDisplayed.value);

const { searchKeywords, recentSearches, deleteRecentSearch } = useSearchService();
const {
  data: searchKeywordsData,
  isLoading: isSearchKeywordsLoading,
  suspense: searchKeywordsSuspense
} = searchKeywords(shouldQuerySearchMeta);
const { data: recentSearchesData, isLoading: isRecentSearchesLoading } =
  recentSearches(shouldQuerySearchMeta);
const { mutateAsync: mutateDeleteRecentSearch } = deleteRecentSearch();

const getDefaultSearchQuery = async () => {
  if (route.query.search) {
    const rawSearch = route.query.search.toString();
    // Decode the URL-encoded search query
    // WM-243 - fix double decoding issue, caused by using % sign in search queries
    return rawSearch;
    return decodeURIComponent(rawSearch);
  } else if (route.params.mailboxId) {
    const flatFolders = await flatFoldersSuspense();
    const mailbox = flatFolders.data?.find((folder) => folder.id === route.params.mailboxId);
    if (mailbox && mailbox.specialUse !== EFolderSystemSpecialUse.INBOX) {
      return `in:"${mailbox.fullName.replace(/"/g, '\\$&')}" `;
    }
  }
  return '';
};

const searchQuery = ref<string | null>(await getDefaultSearchQuery());
const mailboxSearchRef = ref<string | null>(null);
const andOrModel = reactive({
  fromTo: false
});

const searchDropdownCard = ref<InstanceType<typeof VCard> | null>(null);
let searchDropdownCardEventListenerAbortController = new AbortController();

const openAdvancedSearchBus = useEventBus(openAdvancedSearchEventKey);

const isAnyRequestLoading = computed(
  () => isSearchKeywordsLoading.value || isRecentSearchesLoading.value
);

const searchChips = computed<ISearchQuickChip[]>(() => {
  const hasAttachmentChip: ISearchQuickChip = {
    color: 'surface-container-highest',
    type: 'has:attachment',
    label: t('search.chips.hasAttachment')
  };

  const last7DaysChip: ISearchQuickChip = {
    color: 'surface-container-highest',
    type: `after`,
    searchValue: `after:${dayjs().tz(userDetails.value?.timezone).subtract(7, 'day').format('YYYY-MM-DD')}`,
    label: t('search.chips.last7Days')
  };
  const fromMeChip: ISearchQuickChip = {
    color: 'surface-container-highest',
    type: 'from',
    searchValue: `from:${userDetails.value?.address}`,
    label: t('search.chips.fromMe')
  };
  const unreadChip: ISearchQuickChip = {
    color: 'surface-container-highest',
    type: 'is:unread',
    label: t('search.chips.unread')
  };
  const starredChip: ISearchQuickChip = {
    color: 'surface-container-highest',
    type: 'is:starred',
    label: t('search.chips.starred')
  };
  const res = [hasAttachmentChip, last7DaysChip, fromMeChip, unreadChip, starredChip];
  if (!searchKeywordsData.value || !searchQuery.value) {
    return res;
  }
  const matchingKeywords = getMatchingSearchKeywords(searchQuery.value, searchKeywordsData.value);
  return res.map((keywordChip) => {
    const matchingKeyword = Object.keys(matchingKeywords).some(
      (keyword) => keyword === keywordChip.type
    );
    return {
      ...keywordChip,
      color:
        matchingKeyword &&
        (!keywordChip.searchValue ||
          (keywordChip.searchValue && searchQuery.value?.includes(keywordChip.searchValue)))
          ? 'primary'
          : 'surface-container-highest'
    };
  });
});

const sortedRecentSearches = computed<IRecentSearchResponse[]>(() => {
  if (!recentSearchesData.value || recentSearchesData.value.length === 0) {
    return [];
  }
  return [...recentSearchesData.value].sort((a, b) => {
    return b.id - a.id;
  });
});

const latestSearchEmails = computed<IRecentSearchEmailDetails[]>(() => {
  if (
    !sortedRecentSearches.value ||
    sortedRecentSearches.value.length === 0 ||
    !sortedRecentSearches.value[0]
  ) {
    return [];
  }
  return sortedRecentSearches.value[0].searchEmails;
});

const isErrorWithAdvancedSearchDetailsFields = computed(() => {
  return (
    isDisplayingAdvancedSearch.value &&
    Object.keys(errors.value).some(
      (key) =>
        key.includes('query.') &&
        !(key.includes('query.size') && query.size.direction.value === 'off')
    )
  );
});
const isErrorWithAdvancedSearchActionsFields = computed(() => {
  return (
    isDisplayingAdvancedSearch.value &&
    Object.keys(errors.value).some((key) => key.includes('action.') || key.includes('toggles'))
  );
});
const isAnyAdvancedSearchDetailsFieldFilled = computed(() => {
  return (
    isDisplayingAdvancedSearch.value &&
    (!!mailboxSearchRef.value ||
      (Object.keys(query) as (keyof IFilterDetailsModel)[]).some(
        (key) =>
          (key !== 'size' && !!query[key]?.value) ||
          (query.size.value.value && query.size.direction.value !== 'off')
      ))
  );
});
const isAnyAdvancedSearchActionsFieldFilled = computed(() => {
  return (
    isDisplayingAdvancedSearch.value &&
    (Object.keys(actions) as (keyof IFilterActionsModel)[]).some((key) => {
      const value = isRef(actions[key])
        ? toValue(actions[key])
        : Object.values(actions[key]).some((subValue) => toValue(subValue));
      if (key === 'spam' && value === 'nothing') {
        return false;
      }
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }

      return !!value;
    })
  );
});

const calendarView = computed(() => {
  const res = {
    enabled: false,
    keyword: null as 'after' | 'before' | null,
    replaceText: null as string | null,
    selectedDate: null as Dayjs | null
  };

  if (!searchQuery.value || !searchKeywordsData.value) {
    return res;
  }
  const matchingKeywords = getMatchingSearchKeywords(searchQuery.value, searchKeywordsData.value);
  const lowerCaseValue = searchQuery.value.toLowerCase();

  if (lowerCaseValue.endsWith('after:') || lowerCaseValue.endsWith('newer:')) {
    res.enabled = true;
    res.keyword = 'after';
    res.replaceText = lowerCaseValue.endsWith('after:') ? 'after:' : 'newer:';
  } else if (lowerCaseValue.endsWith('before:') || lowerCaseValue.endsWith('older:')) {
    res.enabled = true;
    res.keyword = 'before';
    res.replaceText = lowerCaseValue.endsWith('before:') ? 'before:' : 'older:';
  } else if ('after' in matchingKeywords && lowerCaseValue.endsWith(matchingKeywords.after![0])) {
    res.enabled = true;
    res.keyword = 'after';
    res.replaceText = matchingKeywords.after![0];
    res.selectedDate = dayjs(matchingKeywords.after![1]).isValid()
      ? dayjs(matchingKeywords.after![1])
      : null;
  } else if ('newer' in matchingKeywords && lowerCaseValue.endsWith(matchingKeywords.newer![0])) {
    res.enabled = true;
    res.keyword = 'after';
    res.replaceText = matchingKeywords.newer![0];
    res.selectedDate = dayjs(matchingKeywords.newer![1]).isValid()
      ? dayjs(matchingKeywords.newer![1])
      : null;
  } else if ('before' in matchingKeywords && lowerCaseValue.endsWith(matchingKeywords.before![0])) {
    res.enabled = true;
    res.keyword = 'before';
    res.replaceText = matchingKeywords.before![0];
    res.selectedDate = dayjs(matchingKeywords.before![1]).isValid()
      ? dayjs(matchingKeywords.before![1])
      : null;
  } else if ('older' in matchingKeywords && lowerCaseValue.endsWith(matchingKeywords.older![0])) {
    res.enabled = true;
    res.keyword = 'before';
    res.replaceText = matchingKeywords.older![0];
    res.selectedDate = dayjs(matchingKeywords.older![1]).isValid()
      ? dayjs(matchingKeywords.older![1])
      : null;
  }

  return res;
});

const focusOnSearchInput = () => {
  document.querySelector<HTMLInputElement>('[data-test-id="search-input"] input')?.focus();
};

const onCalendarPicked = (date: unknown) => {
  if (
    !isDayjs(date) ||
    !calendarView.value.enabled ||
    !calendarView.value.keyword ||
    !calendarView.value.replaceText ||
    !searchQuery.value
  ) {
    return;
  }
  searchQuery.value = searchQuery.value.replace(
    calendarView.value.replaceText,
    `${calendarView.value.keyword}:${date.format('YYYY-MM-DD')} `
  );

  focusOnSearchInput();
};

const handleDisplayDropdown = (opened: boolean) => {
  nextTick(() => {
    isSearchDropdownDisplayed.value = opened;
  });
};

watch(
  () => isSearchDropdownDisplayed.value,
  (value) => {
    console.trace('isSearchDropdownDisplayed.value changed', value);
  }
);

const handleDisplayExtendedSearch = (focused: boolean) => {
  isDisplayingAdvancedSearch.value = false;
  handleDisplayDropdown(focused);
};

const handleSearchInputFocus = (event: FocusEvent) => {
  if (isSuppressingRestoredSearchInputFocus) {
    isSuppressingRestoredSearchInputFocus = false;
    // Drop the focus Vuetify restored on close, so the mobile soft
    // keyboard stays dismissed and the search results stay visible
    (event.target as HTMLElement | null)?.blur();
    return;
  }
  handleDisplayExtendedSearch(true);
};

const handleToggleDisplayAdvancedSearch = async (forceState?: boolean) => {
  const newState = forceState ?? !isDisplayingAdvancedSearch.value;
  handleDisplayDropdown(newState);
  isDisplayingAdvancedSearch.value = newState;
  isDisplayingAdvancedSearchDetails.value = true;

  const searchKeywords = await searchKeywordsSuspense();
  if (!searchKeywords.data) {
    // Probably won't happen, but just in case
    return;
  }
  const matchingKeywords = getMatchingSearchKeywords(searchQuery.value ?? '', searchKeywords.data);

  if (!Object.keys(matchingKeywords).length && !searchQuery.value) {
    // Nothing to fill, clear the form to default state
    resetForm();
    return;
  }

  const initialValues: ISettingsParsedFilterItem = {
    id: '',
    enabled: true,
    created: '',
    metaData: {},
    action: {},
    query: {
      from: matchingKeywords.from?.[1],
      to: matchingKeywords.to?.[1],
      subject: cleanMultiWordSearchQuery(matchingKeywords.subject?.[1]),
      ha: 'has:attachment' in matchingKeywords || 'has:attachments' in matchingKeywords,
      size: {
        value: null,
        direction: 'off',
        step: 'KB'
      }
    }
  };

  if ('minSize' in matchingKeywords || 'maxSize' in matchingKeywords) {
    const size = 'minSize' in matchingKeywords ? 'minSize' : 'maxSize';
    const value = matchingKeywords[size]?.[1] ?? '';
    const replacedStep = value.replace(/\d+/g, '');
    const replacedValue = value.replace(replacedStep, '');
    initialValues.query.size = {
      value: parseInt(replacedValue),
      direction: size === 'minSize' ? 'larger' : 'smaller',
      step: isSearchSizeStepValid(replacedStep) ? replacedStep : 'KB'
    };
  }

  andOrModel.fromTo = !!matchingKeywords.fromToAndOr;

  // Remove anything that was matched by the keywords. The leftovers are the search text.
  initialValues.query.text = Object.values(matchingKeywords)
    .reduceRight((acc, value) => {
      return acc.replace(value[0], '');
    }, searchQuery.value ?? '')
    .trim();

  // shouldValidate = false
  setValues(mapFilterFormInitialValues(initialValues), false);
};

const handleToggleChip = (value: string) => {
  if (searchQuery.value?.includes(value)) {
    searchQuery.value = searchQuery.value.replace(value, '');
  } else if (searchQuery.value) {
    searchQuery.value += ' ' + value + ' ';
  } else {
    searchQuery.value = value + ' ';
  }

  focusOnSearchInput();
};

const handleRedirectToSearch = async (newSearchQuery: IRecentSearchResponse['search_query']) => {
  isSearchDropdownDisplayed.value = false;
  if (!newSearchQuery) {
    return;
  }
  await router.push({
    name: ROUTE_NAMES.MAILBOX_SEARCH,
    query: { search: newSearchQuery }
  });
  searchQuery.value = newSearchQuery;
};

const handleDeleteRecentSearch = async (id: IRecentSearchResponse['id']) => {
  await mutateDeleteRecentSearch({ id });
};

const handleRedirectToThread = async (email: IRecentSearchEmailDetails) => {
  await router.push({
    name: ROUTE_NAMES.MAILBOX_SEARCH_THREAD,
    params: { mailboxId: email.mailbox, threadId: email.thread, messageId: email.email_id },
    query: {
      search:
        sortedRecentSearches.value &&
        sortedRecentSearches.value.length &&
        sortedRecentSearches.value[0]!.search_query
          ? sortedRecentSearches.value[0]!.search_query
          : undefined
    }
  });
  isSearchDropdownDisplayed.value = false;
};

const handleActionToggleCreateFilter = () => {
  isDisplayingAdvancedSearchDetails.value = !isDisplayingAdvancedSearchDetails.value;
};

const handleActionSearch = () => {
  if (!isDisplayingAdvancedSearch.value) {
    handleRedirectToSearch(searchQuery.value ?? '');
    return;
  }
  // Gather-up the fields and set the searchQuery.value

  let queryString = '';
  (Object.keys(query) as (keyof IFilterDetailsModel)[]).forEach((key) => {
    switch (key) {
      case 'from':
        queryString += query[key].value
          ? `from:${query[key].value} ${
              // if there is a value in the "to" field, and the from-to switch is on, add the OR keyword
              !andOrModel.fromTo || !query.to.value ? '' : 'OR '
            }`
          : '';
        break;
      case 'to':
        queryString += query[key].value ? `to:${query[key].value} ` : '';
        break;
      case 'subject':
        queryString += query[key].value ? `subject:${addMultiWordGrouping(query[key].value)} ` : '';
        break;
      case 'ha':
        queryString += query[key].value ? `has:attachments ` : '';
        break;
      case 'size':
        if (!query.size.value.value) {
          // consider this empty
          break;
        }
        if (query.size.direction.value === 'smaller') {
          queryString += `maxSize:${query.size.value.value}${query.size.step.value} `;
        } else if (query.size.direction.value === 'larger') {
          queryString += `minSize:${query.size.value.value}${query.size.step.value} `;
        }
        break;
      case 'text':
        queryString += query[key].value ? `${query[key].value} ` : '';
        break;
      case 'listId':
        queryString += query[key].value ? `${query[key].value} ` : '';
        break;
      // default:
      //   queryString += query[key].value ? `${query[key].value} ` : '';
      //   break;
    }
  });

  if (mailboxSearchRef.value) {
    queryString += `in:"${mailboxSearchRef.value.replace(/"/g, '\\$&')}" `;
  }

  handleRedirectToSearch(queryString.trim());
};

const handleActionSubmitFilter = async () => {
  const isSuccess = await handleSubmitFilter();
  if (isSuccess) {
    resetForm();
    isDisplayingAdvancedSearch.value = false;
    isDisplayingAdvancedSearchDetails.value = true;
  }
};

watch(
  () => isSearchDropdownDisplayed.value,
  (value) => {
    if (value) {
      nextTick(() => {
        searchDropdownCardEventListenerAbortController = new AbortController();

        let currentFocus: keyof ISearchFocusIndices = 'quickChip';
        const focusIndices: ISearchFocusIndices = {
          quickChip: 1,
          recentSearch: 1,
          latestSearchEmail: 1
        };
        (searchDropdownCard.value?.$el as HTMLElement)?.addEventListener(
          'keydown',
          (event) => {
            if (isDisplayingAdvancedSearch.value) {
              return;
            }
            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
              event.preventDefault();
              event.stopPropagation();
              if (event.key === 'ArrowUp') {
                if (focusIndices.quickChip >= 0 && currentFocus === 'quickChip') {
                  focusIndices.quickChip--;
                  if (focusIndices.quickChip <= 0) {
                    currentFocus = 'latestSearchEmail';
                    focusIndices.latestSearchEmail = latestSearchEmails.value.length;
                  }
                } else if (focusIndices.recentSearch >= 0 && currentFocus === 'recentSearch') {
                  focusIndices.recentSearch--;
                  if (focusIndices.recentSearch <= 0) {
                    currentFocus = 'quickChip';
                    focusIndices.quickChip = searchChips.value.length;
                  }
                } else if (
                  focusIndices.latestSearchEmail >= 0 &&
                  currentFocus === 'latestSearchEmail'
                ) {
                  focusIndices.latestSearchEmail--;
                  if (focusIndices.latestSearchEmail <= 0) {
                    currentFocus = 'recentSearch';
                    focusIndices.recentSearch = sortedRecentSearches.value.length;
                  }
                }
              } else if (event.key === 'ArrowDown') {
                if (
                  focusIndices.quickChip <= searchChips.value.length &&
                  currentFocus === 'quickChip'
                ) {
                  focusIndices.quickChip++;
                  if (focusIndices.quickChip > searchChips.value.length) {
                    currentFocus = 'recentSearch';
                    focusIndices.recentSearch = 1;
                  }
                } else if (
                  focusIndices.recentSearch <= sortedRecentSearches.value.length &&
                  currentFocus === 'recentSearch'
                ) {
                  focusIndices.recentSearch++;
                  if (focusIndices.recentSearch > sortedRecentSearches.value.length) {
                    currentFocus = 'latestSearchEmail';
                    focusIndices.latestSearchEmail = 1;
                  }
                } else if (
                  focusIndices.latestSearchEmail <= latestSearchEmails.value.length &&
                  currentFocus === 'latestSearchEmail'
                ) {
                  focusIndices.latestSearchEmail++;
                  if (focusIndices.latestSearchEmail > latestSearchEmails.value.length) {
                    currentFocus = 'quickChip';
                    focusIndices.quickChip = 1;
                  }
                }
              }

              const focusElement = document.querySelector(
                `[data-search-focus="${currentFocus}"]:nth-child(${focusIndices[currentFocus]})`
              );
              if (focusElement) {
                (focusElement as HTMLElement).focus();
              }
            }
          },
          { signal: searchDropdownCardEventListenerAbortController.signal }
        );
      });
    } else {
      // when closed remove the event listener
      searchDropdownCardEventListenerAbortController.abort();
      // Vuetify restores focus to the search input in a post-flush watcher
      // after the dropdown closes. Arm the suppression so that focus does
      // not reopen the dropdown; clear it after nextTick, which runs after
      // the focus restoration has happened.
      isSuppressingRestoredSearchInputFocus = true;
      nextTick(() => {
        isSuppressingRestoredSearchInputFocus = false;
      });
    }
  }
);

watch(
  () => route.params.mailboxId,
  async () => {
    searchQuery.value = await getDefaultSearchQuery();
  }
);

watch(
  () => route.query.search,
  async () => {
    searchQuery.value = await getDefaultSearchQuery();
  }
);

const isSearchDropdownPersistent = ref(false);
registerToggleSearchContainer((open: boolean) => {
  isSearchDropdownDisplayed.value = open;
  isSearchDropdownPersistent.value = open;
});

openAdvancedSearchBus.on(() => {
  nextTick();
  handleToggleDisplayAdvancedSearch(true);
});
</script>

<template>
  <v-menu
    v-model="isSearchDropdownDisplayed"
    :close-on-content-click="false"
    :location-strategy="'connected'"
    :max-width="0"
    :min-width="'auto'"
    :open-on-click="false"
    :open-on-focus="false"
    :capture-focus="false"
    :transition="{
      name: 'slide-y-transition',
      onAfterLeave: () => {
        // Handle this here because otherwise we get UI flickering between states
        handleToggleDisplayAdvancedSearch(false);
      }
    }"
    :persistent="isSearchDropdownPersistent"
    no-click-animation
    class="search__dropdown-menu"
    content-class="search__dropdown"
    location="bottom"
    z-index="2000"
  >
    <template #activator="{ props: menuActivatorProps }">
      <div
        :class="{ 'search__container--opened': isSearchDropdownDisplayed }"
        class="search__container"
        v-bind="menuActivatorProps"
      >
        <VTextField
          v-model="searchQuery"
          :label="t('search.label')"
          :prepend-icon="
            props.isSmallDisplay
              ? isSearchDropdownDisplayed
                ? 'mso:arrow_back'
                : '$menu'
              : undefined
          "
          :prepend-inner-icon="props.isSmallDisplay ? undefined : 'mdi-magnify'"
          bg-color="#FFFFFF00"
          class="search"
          clear-icon="mso:close"
          clearable
          data-test-id="search-input"
          data-onboarding="search-input"
          flat
          hide-details
          single-line
          variant="solo"
          @click="() => handleDisplayExtendedSearch(true)"
          @focus="handleSearchInputFocus"
          @click:prepend.prevent.stop="
            props.isSmallDisplay
              ? isSearchDropdownDisplayed
                ? handleDisplayExtendedSearch(false)
                : emit('toggleSidebar')
              : undefined
          "
          @keydown.enter="handleActionSearch"
        >
        </VTextField>
        <AccountsDropdown v-if="props.isSmallDisplay && !isSearchDropdownDisplayed" />
        <VTooltip
          v-else-if="!props.isSmallDisplay"
          :text="
            isDisplayingAdvancedSearch
              ? t('search.closeAdvancedSearch')
              : t('search.openAdvancedSearch')
          "
        >
          <template #activator="{ props: tooltipActivatorProp }">
            <TextBtn
              class="search__advanced-search-activator"
              data-test-id="open-advanced-search"
              size="40"
              sentry-unmask
              v-bind="tooltipActivatorProp"
              @click.stop="() => handleToggleDisplayAdvancedSearch()"
            >
              <VIcon color="search-bar-text">mso:tune</VIcon>
            </TextBtn>
          </template>
        </VTooltip>
      </div>
    </template>
    <v-card
      ref="searchDropdownCard"
      :variant="props.isSmallDisplay ? 'flat' : undefined"
      class="search__dropdown-card"
      data-test-id="search-dropdown-card"
    >
      <template v-if="calendarView.enabled">
        <v-date-picker
          width="100%"
          control-variant="modal"
          hide-header
          show-adjacent-months
          :model-value="calendarView.selectedDate"
          @update:model-value="onCalendarPicked"
        ></v-date-picker>
      </template>
      <template v-else-if="isDisplayingAdvancedSearch">
        <VCardText class="pa-0">
          <v-row class="align-center justify-space-between mb-4 position-relative" no-gutters>
            <TextBtn
              class="text-body-lg py-0 px-1"
              color="on-surface"
              sentry-unmask
              density="compact"
              @click="
                () =>
                  isDisplayingAdvancedSearchDetails
                    ? handleDisplayExtendedSearch(true)
                    : handleActionToggleCreateFilter()
              "
            >
              <v-icon>mso:arrow_back</v-icon>
              {{ isDisplayingAdvancedSearchDetails ? t('search.label') : t('common.cancel') }}
            </TextBtn>
            <SearchInstructions />
          </v-row>
          <FilterDetails
            v-if="isDisplayingAdvancedSearchDetails"
            v-model="query"
            v-model:mailbox="mailboxSearchRef"
            v-model:from-to-and-or="andOrModel.fromTo"
            :errors="errors"
            :is-pending="isPending"
            is-search-view
          />

          <template v-else>
            <FilterActions v-model="actions" :errors="errors" :is-pending="isPending" />
          </template>
        </VCardText>
      </template>
      <template v-else>
        <div
          v-if="isAnyRequestLoading"
          class="d-flex justify-center pa-4"
          data-onboarding="search-loading"
        >
          <v-progress-circular indeterminate></v-progress-circular>
        </div>
        <template v-else>
          <div class="quick-chip__wrapper">
            <v-row class="quick-chip__container" no-gutters>
              <v-chip
                v-for="chip in searchChips"
                :key="chip.type"
                :color="chip.color"
                class="quick-chip"
                data-search-focus="quickChip"
                variant="flat"
                sentry-unmask
                @click="() => handleToggleChip(chip.searchValue ?? chip.type)"
                >{{ chip.label }}
              </v-chip>
            </v-row>

            <SearchInstructions v-if="mobile" />
          </div>

          <template v-if="sortedRecentSearches.length">
            <v-divider v-if="!props.isSmallDisplay"></v-divider>
            <span class="text-label-lg" sentry-unmask>{{
              t('search.extended.recentSearches')
            }}</span>
            <v-list class="search-list recent-searches__list">
              <v-list-item
                v-for="search in sortedRecentSearches"
                :key="`${search.id}-${search.search_query}`"
                class="search-list__item recent-searches__list-item"
                data-search-focus="recentSearch"
                density="compact"
                prepend-icon="mso:schedule"
                @click="() => handleRedirectToSearch(search.search_query)"
              >
                <span>
                  {{ search.search_query }}
                </span>
                <CloseBtn size="24" @click.stop="() => handleDeleteRecentSearch(search.id)">
                  <v-icon class="opacity-60">mso:close</v-icon>
                </CloseBtn>
              </v-list-item>
            </v-list>
          </template>
          <template v-if="latestSearchEmails.length">
            <v-divider v-if="!props.isSmallDisplay"></v-divider>
            <span class="text-label-lg">{{ t('search.extended.recentlySearchedMails') }}</span>
            <v-list class="search-list last-search__list">
              <v-list-item
                v-for="email in latestSearchEmails"
                :key="email.email_id"
                class="search-list__item last-search__list-item"
                data-search-focus="latestSearchEmail"
                density="compact"
                prepend-icon="mdi-email-open"
                @click="() => handleRedirectToThread(email)"
              >
                <div class="d-flex justify-space-between ga-4 w-100">
                  <div class="d-flex flex-column overflow-hidden">
                    <span>
                      <span v-if="email.is_draft" class="mr-1 text-error" sentry-unmask>
                        [{{ t('mailbox.listing.message.draft') }}]
                      </span>
                      {{ email.subject }}
                    </span>
                    <span class="text-body-md last-search__item-address">{{ email.address }}</span>
                  </div>

                  <div
                    v-if="email.has_attachments || email.date"
                    class="d-flex ga-2 align-center text-body-md"
                  >
                    <span v-if="email.has_attachments">
                      <v-icon>mso:attachment</v-icon>
                    </span>
                    <MailboxItemDate
                      v-if="email.date"
                      :date="email.date"
                      class="last-search__item-date"
                    />
                  </div>
                </div>
              </v-list-item>
            </v-list>
          </template>
          <v-divider v-if="!props.isSmallDisplay"></v-divider>
        </template>
      </template>

      <div
        class="d-flex flex-column flex-sm-row justify-center ga-4 search-action__container"
        v-if="!calendarView.enabled"
      >
        <TextBtn
          v-if="props.isSmallDisplay"
          :to="{ name: ROUTE_NAMES.SETTINGS_FILTERS, params: { userIndex } }"
          class="search-action"
          sentry-unmask
        >
          {{ t('search.advanced.createFilter') }}
        </TextBtn>
        <OutlinedBtn
          v-if="!props.isSmallDisplay && isDisplayingAdvancedSearch"
          :disabled="
            isErrorWithAdvancedSearchDetailsFields || !isAnyAdvancedSearchDetailsFieldFilled
          "
          class="search-action"
          data-test-id="search-create-filter"
          @click="handleActionToggleCreateFilter"
          sentry-unmask
        >
          {{
            isDisplayingAdvancedSearchDetails
              ? t('search.advanced.createFilter')
              : t('common.cancel')
          }}
        </OutlinedBtn>
        <FilledBtn
          v-if="
            !isDisplayingAdvancedSearch ||
            (isDisplayingAdvancedSearch && isDisplayingAdvancedSearchDetails)
          "
          :disabled="
            isErrorWithAdvancedSearchDetailsFields ||
            (!isDisplayingAdvancedSearch && !searchQuery) ||
            (isDisplayingAdvancedSearch && !isAnyAdvancedSearchDetailsFieldFilled)
          "
          class="search-action"
          data-test-id="search-handle"
          @click="handleActionSearch"
          sentry-unmask
        >
          {{ t('search.label') }}
        </FilledBtn>

        <FilledBtn
          v-else
          :disabled="
            !isAnyAdvancedSearchActionsFieldFilled ||
            isErrorWithAdvancedSearchActionsFields ||
            isErrorWithAdvancedSearchDetailsFields
          "
          data-test-id="search-save-filter"
          @click.prevent="handleActionSubmitFilter"
          sentry-unmask
        >
          {{ t('settings.filters.form.save') }}
        </FilledBtn>

        <SearchInstructions v-if="!mobile" absolute />
      </div>
    </v-card>
  </v-menu>
</template>

<style lang="scss">
.search__dropdown-menu {
  z-index: 2000 !important;
}
</style>

<style lang="scss" scoped>
@use '../../assets/styles/settings' as settings;
@use 'sass:map';

.search {
  :deep(.v-field__prepend-inner),
  :deep(.v-field__append-inner),
  :deep(.v-field__clearable) {
    color: rgb(var(--v-theme-alert-close));

    .v-icon {
      --v-medium-emphasis-opacity: 1;

      color: rgb(var(--v-theme-search-bar-text));
    }
  }

  :deep(.v-input__prepend) {
    margin: 0 0.75rem;
  }

  &__container {
    display: flex;
    align-items: center;
    color: rgb(var(--v-theme-search-bar-text));
    background-color: rgb(var(--v-theme-search-bar));
    width: 100%;
    border-radius: 0.5rem;
    margin: 0 1rem;
    border: 1px solid rgb(var(--v-theme-outline-variant));

    @media screen and #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      max-width: 33.75rem;
      margin: 0 2.75rem;
      border: none;
    }

    &--opened {
      border: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;

      @media screen and #{map.get(settings.$display-breakpoints, 'xs')} {
        margin: 0;
        position: fixed;
        top: 0;
        left: 0;
        max-width: 100vw;
        height: 100%;
      }

      &::after {
        content: '';
        border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 0;
        background: rgb(var(--v-theme-search-bar));

        @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
          bottom: -0.625rem;
          height: 0.6875rem;
        }
      }

      @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
        height: auto;
        position: relative;
      }
    }
  }

  &__advanced-search {
    &-activator {
      padding: 0.5rem;
    }
  }

  &-list {
    display: flex;
    flex-direction: column;
    background: transparent;
    padding: 0;

    &__item {
      min-height: 1.5rem;
      padding: 0.25rem 0 !important;
      border-radius: 0.5rem !important;

      :deep(.v-list-item__content) {
        justify-content: space-between;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        overflow: hidden;
      }

      :deep(.v-list-item__prepend > .v-icon ~ .v-list-item__spacer),
      :deep(.v-list-item__append > .v-icon ~ .v-list-item__spacer) {
        width: 1rem;
      }
    }
  }

  &-action {
    min-width: 9.375rem;

    &__container {
      position: relative;
    }
  }
}

:deep(.search__dropdown) {
  @media screen and #{map.get(settings.$display-breakpoints, 'xs')} {
    top: 2.875rem !important;
    left: 0 !important;
  }

  .search__dropdown-card {
    padding-top: 1rem;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: calc(100dvh - 6rem) !important;

    @media screen and #{map.get(settings.$display-breakpoints, 'xs')} {
      min-height: calc(100dvh - 2.875rem);
      width: 100vw;
      border-radius: 0;
      box-shadow:
        0 0 0 0 var(--v-shadow-key-umbra-opacity, rgb(0 0 0 / 20%)),
        0 0 0 0 var(--v-shadow-key-penumbra-opacity, rgb(0 0 0 / 14%)),
        0 0 0 0 var(--v-shadow-key-ambient-opacity, rgb(0 0 0 / 12%)) !important;
    }

    @media screen and #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
      margin-top: 0.625rem;
    }
  }
}

.quick-chip {
  border-radius: 0.5rem !important;

  &__wrapper {
    position: relative;
    overflow-x: auto;
    display: flex;
    flex-flow: row nowrap;

    @media screen and #{map.get(settings.$display-breakpoints, 'xs')} {
      margin: 0 -1.5rem;
      padding: 0 1.5rem;
    }
  }

  &__container {
    gap: 0.5rem;
    flex-grow: 0;

    @media screen and #{map.get(settings.$display-breakpoints, 'xs')} {
      width: fit-content;
    }
  }
}

.recent-searches {
  &__list {
    &-item {
      :deep(.v-list-item__content) {
        span {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }
    }
  }
}

.last-search {
  &__list {
    &-item {
      :deep(.v-list-item__content) {
        span {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }
      }

      :deep(.v-list-item__prepend > .v-icon) {
        --v-medium-emphasis-opacity: 1;

        color: rgb(var(--v-theme-primary));
      }
    }
  }

  &__item {
    &-address,
    &-date {
      letter-spacing: 0 !important;
    }

    &-date {
      white-space: nowrap;
    }
  }
}
</style>
